import {TooltipItemType, TooltipRow} from 'common/components/chart-tooltip/types';
import {commaizeNumber} from 'common/util';
import {TextLabelFormatter} from 'common/report/models/funnels/bar-chart/types';

import {FrequencyViewType} from './types';

export function getBucketLabel(bucketStart: number, bucketEnd?: number) {
  return bucketEnd && bucketEnd - bucketStart > 1 ? `${bucketStart} - ${bucketEnd}` : `${bucketStart}`;
}

export function getToolTipText(
  count: number,
  percentage: number,
  bucketLabel: string,
  plural: boolean,
  stepName: string,
  viewType: FrequencyViewType,
): TooltipRow[] {
  const bucketUnit = plural ? `times` : `time`;
  const viewTypeDetails = tooltipViewDetails(viewType);
  return [
    {
      items: [
        {text: `${percentage}%`, type: TooltipItemType.Data},
        {text: `${viewTypeDetails.tooltipPreStepText} ${stepName}`, type: TooltipItemType.Text},
        {text: bucketLabel, type: TooltipItemType.Data},
        {
          text: `${bucketUnit} ${viewTypeDetails.tooltipPostStepText}`,
          type: TooltipItemType.Text,
        },
      ],
    },
    {
      items: [
        {text: commaizeNumber(count), type: TooltipItemType.Data},
        {text: viewTypeDetails.tooltipCountUnit, type: TooltipItemType.Text},
      ],
    },
  ];
}

export function getMedianToolTipText(median: string): TooltipRow[] {
  return [
    {
      items: [
        {text: median, type: TooltipItemType.Data},
        {text: `median events`, type: TooltipItemType.Text},
      ],
    },
  ];
}

function getMedianFormatter(median: number) {
  return `${median ? median : `N/A`}`;
}

export const partialFormatter: TextLabelFormatter = {
  getBucketLabel,
  getMedianFormatter,
  getMedianToolTipText,
};

export enum FunnelStepBucketAccessors {
  DropoffStepFreqBuckets = `dropoff_step_freq_buckets`,
  DropoffStepFreqPercentiles = `dropoff_step_freq_percentiles`,
  ConvertStepFreqBuckets = `convert_step_freq_buckets`,
  ConvertStepFreqPercentiles = `convert_step_freq_percentiles`,
  ConvertRateFreqBuckets = `convert_rate_freq_buckets`,
  ConvertRateFreqPercentiles = `convert_rate_freq_percentiles`,
}

// Wrapper to more neatly toggle between strings based on conversion type
export function conversionMode(type = FrequencyViewType.Conversion) {
  switch (type) {
    case FrequencyViewType.Conversion:
      return {
        bucketAccessor: FunnelStepBucketAccessors.ConvertStepFreqBuckets,
        medianAccessor: FunnelStepBucketAccessors.ConvertStepFreqPercentiles,
        representationText: `conversions`,
      } as const;
    case FrequencyViewType.ConversionRate:
      return {
        bucketAccessor: FunnelStepBucketAccessors.ConvertRateFreqBuckets,
        medianAccessor: FunnelStepBucketAccessors.ConvertRateFreqPercentiles,
        representationText: `conversion rate`,
      } as const;
    default:
      return {
        bucketAccessor: FunnelStepBucketAccessors.DropoffStepFreqBuckets,
        medianAccessor: FunnelStepBucketAccessors.DropoffStepFreqPercentiles,
        representationText: `drop-offs`,
      } as const;
  }
}

// Gives tooltip details for ToolTipText based on FrequencyViewType
// @param {FrequencyViewType} type - the type of Frequency Chart
// @return {string, string, string}
// {tooltipCountUnit, tooltipPreStepText, tooltipPostStepText}
export function tooltipViewDetails(type = FrequencyViewType.Conversion) {
  switch (type) {
    case FrequencyViewType.Conversion:
      return {
        tooltipCountUnit: `conversions`,
        tooltipPreStepText: `who did`,
        tooltipPostStepText: `before next step`,
      } as const;
    case FrequencyViewType.ConversionRate:
      return {
        tooltipCountUnit: `users converted`,
        tooltipPreStepText: `of all users that converted did`,
        tooltipPostStepText: ``,
      } as const;
    default:
      return {
        tooltipCountUnit: `drop offs`,
        tooltipPreStepText: `who did`,
        tooltipPostStepText: `before dropping off`,
      } as const;
  }
}
